import axios from 'axios';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { parseInt, toString, trimEnd, trimStart } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { msalInstance, loginRequest, logoutRequest } from '../../config/msalConfig';

/* eslint-disable @typescript-eslint/naming-convention */
declare module 'axios' {
    interface InternalAxiosRequestConfig {
        skipLoader?: boolean;
        _retried?: boolean;
    }

    interface AxiosRequestConfig {
        skipLoader?: boolean;
        _retried?: boolean;
    }
}

const quotes: string[] = [
    'If you are considerate of people, all will go well in business.',
    'Do not seek profit; seek to serve society.',
    'Make products that are useful to society.',
    'Continue to take on new challenges without fear of failure.',
    'Honesty and sincerity are the foundation of trust.',
];

let lastQuoteIndex = -1;
let activeRequests = 0;
let quoteInterval: ReturnType<typeof setInterval> | null = null;

function getNextQuoteIndex(): number {
    if (quotes.length <= 1) {
        return 0;
    }

    let index = Math.floor(Math.random() * quotes.length);

    while (index === lastQuoteIndex) {
        index = Math.floor(Math.random() * quotes.length);
    }

    return index;
}

function setRandomQuote(): void {
    const quoteEl = document.getElementById('loader-quote-text');

    if (!quoteEl) {
        return;
    }

    const index = getNextQuoteIndex();
    lastQuoteIndex = index;

    quoteEl.innerHTML = `
        “${quotes[index]}”
        <span>— Gonshiro Kubota</span>
    `;
}

function startQuoteRotation(): void {
    setRandomQuote();

    if (quoteInterval) {
        return;
    }

    quoteInterval = setInterval(() => {
        setRandomQuote();
    }, 3000);
}

function stopQuoteRotation(): void {
    if (!quoteInterval) {
        return;
    }

    clearInterval(quoteInterval);
    quoteInterval = null;
}

function showLoader(): void {
    const loader = document.getElementById('global-loader');

    if (!loader) {
        return;
    }

    loader.style.display = 'flex';
    startQuoteRotation();
}

function hideLoader(): void {
    const loader = document.getElementById('global-loader');

    if (loader) {
        loader.style.display = 'none';
    }

    stopQuoteRotation();
}

function startLoading(): void {
    activeRequests++;
    showLoader();
}

function stopLoading(): void {
    activeRequests = Math.max(0, activeRequests - 1);

    if (activeRequests === 0) {
        hideLoader();
    }
}

const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT),
    withCredentials: true,
});

apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        const token = localStorage.getItem('accessToken');

        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${trimStart(trimEnd(token, '"'), '"')}`;
        }

        if (!config.skipLoader) {
            startLoading();
        }

        return config;
    },
    (error: unknown): Promise<never> => {
        const config = (error as AxiosError)?.config as InternalAxiosRequestConfig | undefined;

        if (!config?.skipLoader) {
            stopLoading();
        }

        return Promise.reject(error);
    }
);

apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (['post', 'put', 'patch'].includes(config.method || '')) {
            config.headers = config.headers || {};
            config.headers['Idempotency-Key'] = uuidv4();
        }

        return config;
    },
    (error: unknown): Promise<never> => Promise.reject(error)
);

apiClient.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
        const config = response.config as InternalAxiosRequestConfig;

        if (!config?.skipLoader) {
            stopLoading();
        }

        return response;
    },
    async (axiosError: AxiosError<IApiErrorResponse>): Promise<never> => {
        const config = axiosError.config as InternalAxiosRequestConfig | undefined;

        if (!config?.skipLoader) {
            stopLoading();
        }

        // On 401, attempt a silent token refresh and retry the request once
        if (axiosError.response?.status === 401 && config && !config._retried) {
            config._retried = true;

            try {
                if (msalInstance) {
                    const accounts = msalInstance.getAllAccounts();

                    if (accounts.length) {
                        const refreshed = await msalInstance.acquireTokenSilent({
                            scopes: loginRequest.scopes,
                            account: accounts[0],
                            forceRefresh: true,
                        });

                        const newToken = toString(refreshed.idToken);
                        localStorage.setItem('accessToken', newToken);
                        localStorage.setItem('token', newToken);

                        config.headers = config.headers || {};
                        config.headers.Authorization = `Bearer ${newToken}`;

                        return apiClient(config) as unknown as never;
                    }
                }
            } catch {
                // Silent refresh failed — fall through to forced logout
            }

            // Could not refresh — clear tokens and redirect to B2C login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('token');

            if (msalInstance) {
                const activeAccount = msalInstance.getActiveAccount();
                if (activeAccount) {
                    logoutRequest.account = activeAccount;
                }
                await msalInstance.logoutRedirect(logoutRequest);
            }
        }

        return Promise.reject(axiosError);
    }
);

export default apiClient;

export interface IApiErrorResponse {
    title: string;
    detail: string;
    status: number;
}
